import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from './PageHeader';
import Breadcrumbs from './Breadcrumbs';

export default function PrivacyPolicy({ t, lang, setLang }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isGerman = lang === 'de';

  const breadcrumbItems = [
    { label: t('breadcrumb_home') || 'Home', path: '/' },
    { label: isGerman ? 'Datenschutz' : 'Privacy Policy', path: null },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <PageHeader lang={lang} setLang={setLang} t={t} />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mt-8 space-y-8">
          <h1 className="text-3xl font-bold text-white mb-6">
            {isGerman ? 'Datenschutzerklärung' : 'Privacy Policy'}
          </h1>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '1. Verantwortlicher' : '1. Data Controller'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Der Betreiber von Clansight ist verantwortlich für die Verarbeitung personenbezogener Daten. Die Kontaktdaten des Betreibers finden Sie im Impressum. Bei Fragen zum Datenschutz können Sie den Betreiber über die dort angegebenen Kontaktmöglichkeiten oder über Discord kontaktieren.'
                : 'The operator of Clansight is responsible for the processing of personal data. The contact details of the operator can be found in the Legal Notice (Imprint). For questions regarding data protection, you can contact the operator via the contact information provided there or through Discord.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '2. Allgemeine Hinweise' : '2. General Information'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Clansight ist ein unabhängiges Community-Projekt zur Darstellung von Clash of Clans Clan-Statistiken. Clansight steht in keiner Verbindung zu Supercell, Clash of Clans oder Discord. Die Nutzung der Website erfolgt freiwillig.'
                : 'Clansight is an independent community project for displaying Clash of Clans clan statistics. Clansight is not affiliated with Supercell, Clash of Clans, or Discord. The use of the website is voluntary.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '3. Verarbeitete Daten' : '3. Data We Process'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Wir verarbeiten personenbezogene Daten, die für den Betrieb der Website und Bereitstellung ihrer Funktionen notwendig sind.'
                : 'We process personal data that is required to operate the website and provide its functions.'}
            </p>

            <div className="ml-4 space-y-4">
              <h3 className="font-medium text-white">
                {isGerman ? 'Benutzerkonto' : 'User Account Data'}
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>Discord ID</li>
                <li>{isGerman ? 'Discord-Benutzername (sofern verfügbar)' : 'Discord username (where available)'}</li>
                <li>{isGerman ? 'E-Mail-Adresse, sofern vom Authentifizierungsdienst bereitgestellt' : 'Email address, if provided through the authentication provider'}</li>
                <li>{isGerman ? 'Technische Informationen im Zusammenhang mit Login und Authentifizierung' : 'Technical information related to login and authentication'}</li>
              </ul>
              <p className="text-gray-300 text-sm">
                {isGerman
                  ? 'Diese Daten werden zur Authentifizierung von Nutzern und Verwaltung von Accounts verwendet.'
                  : 'This data is used to authenticate users and manage accounts.'}
              </p>

              <h3 className="font-medium text-white pt-2">
                {isGerman ? 'Clash of Clans Daten' : 'Clash of Clans Data'}
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>{isGerman ? 'Spieler-Tag' : 'Player Tag'}</li>
                <li>{isGerman ? 'Clan-Informationen' : 'Clan information'}</li>
                <li>{isGerman ? 'Angriffe' : 'Attacks'}</li>
                <li>{isGerman ? 'Verteidigungen' : 'Defenses'}</li>
                <li>{isGerman ? 'Kriegsergebnisse' : 'War results'}</li>
                <li>{isGerman ? 'Punktzahlen' : 'Scores'}</li>
                <li>{isGerman ? 'Ranglisteninformationen' : 'Ranking information'}</li>
              </ul>
              <p className="text-gray-300 text-sm">
                {isGerman
                  ? 'Diese Daten können über öffentlich verfügbare Clash of Clans APIs bezogen oder von Nutzern bereitgestellt werden.'
                  : 'This data may be obtained through publicly available Clash of Clans APIs or provided by users.'}
              </p>

              <h3 className="font-medium text-white pt-2">
                {isGerman ? 'Kommunikationsdaten' : 'Communication Data'}
              </h3>
              <p className="text-gray-300 text-sm">
                {isGerman
                  ? 'Wenn Nutzer den Betreiber kontaktieren: Nachrichteninhalt, Kontaktinformationen, technische Informationen im Zusammenhang mit der Anfrage.'
                  : 'If users contact the operator: message content, contact information, technical information related to the request.'}
              </p>

              <h3 className="font-medium text-white pt-2">
                {isGerman ? 'Zukünftige kostenpflichtige Funktionen' : 'Future Paid Features'}
              </h3>
              <p className="text-gray-300 text-sm">
                {isGerman
                  ? 'Falls kostenpflichtige Funktionen eingeführt werden, können zusätzliche Daten verarbeitet werden: Zahlungsstatus, Abonnementinformationen, notwendige Rechnungsinformationen. Vollständige Zahlungsdaten wie Kreditkartennummern werden nicht gespeichert.'
                  : 'If paid features are introduced, additional data may be processed: payment status, subscription information, billing information where required. Clansight does not store complete payment details such as credit card numbers.'}
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '4. Zweck der Verarbeitung' : '4. Purpose of Processing'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Daten werden verarbeitet für: Bereitstellung der Website-Funktionalität, Anzeige von Clan-Statistiken, Erstellung von Ranglisten, Verwaltung von Benutzeraccounts, Zugriffskontrolle, Website-Sicherheit, Fehleranalyse, optionale Discord-Benachrichtigungen.'
                : 'Data is processed for: providing website functionality, displaying clan statistics, creating rankings, managing user accounts, access control, website security, error analysis, optional Discord notifications.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '5. Rechtsgrundlage' : '5. Legal Basis'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Die Verarbeitung erfolgt aufgrund: Bereitstellung der vom Nutzer gewünschten Dienste, Nutzereinwilligung, berechtigtes Interesse des Betreibers an einem sicheren und funktionsfähigen Dienst. Wo anwendbar, unterliegt die Verarbeitung auch der EU-Datenschutz-Grundverordnung (DSGVO).'
                : 'Processing is based on: providing the services requested by the user, user consent, legitimate interests of the operator to maintain a secure and functional service. Where applicable, processing may also be subject to the EU General Data Protection Regulation (GDPR).'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '6. Weitergabe' : '6. Data Sharing'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Personenbezogene Daten werden nicht verkauft. Daten können mit folgenden Dienstleistern geteilt werden:'
                : 'Personal data is not sold. Data may be shared with the following service providers:'}
            </p>
            <div className="ml-4 space-y-4">
              <div>
                <h3 className="font-medium text-white">Supabase</h3>
                <p className="text-gray-300 text-sm">
                  {isGerman
                    ? 'Verwendet für: Authentifizierung, Datenbank-Dienste, Daten-Hosting'
                    : 'Used for: authentication, database services, data hosting'}
                </p>
              </div>
              <div>
                <h3 className="font-medium text-white">Discord</h3>
                <p className="text-gray-300 text-sm">
                  {isGerman
                    ? 'Verwendet für: Login-Authentifizierung, optionale Benachrichtigungen'
                    : 'Used for: login authentication, optional notifications'}
                </p>
              </div>
              <div>
                <h3 className="font-medium text-white">Supercell / Clash of Clans API</h3>
                <p className="text-gray-300 text-sm">
                  {isGerman
                    ? 'Verwendet für: Abruf von Spieler- und Clan-Statistiken'
                    : 'Used for: retrieving player and clan statistics'}
                </p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm mt-4">
              {isGerman
                ? 'Wo erforderlich werden angemessene Datenschutzmassnahmen angewendet.'
                : 'Appropriate data protection measures are applied where required.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '7. Speicherung und Löschung' : '7. Storage and Deletion'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Personenbezogene Daten werden nur so lange gespeichert wie notwendig. Im Allgemeinen: Kontodaten bis zur Account-Löschung; Statistiken können für historische Ranglisten erhalten bleiben; Kommunikationsdaten bis zur Bearbeitung der Anfrage. Rechtliche Verpflichtungen bleiben unberührt.'
                : 'Personal data is stored only as long as necessary. Generally: account data until account deletion; statistics may remain stored for historical rankings; communication data until the request has been processed. Legal obligations remain unaffected.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '8. Nutzerrechte' : '8. User Rights'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Nutzer haben nach geltendem Datenschutzrecht folgende Rechte:'
                : 'Users have the following rights under applicable data protection law:'}
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>{isGerman ? 'Auskunftsrecht' : 'Right of access'}</li>
              <li>{isGerman ? 'Berichtigungsrecht' : 'Right to correction'}</li>
              <li>{isGerman ? 'Löschrecht' : 'Right to deletion'}</li>
              <li>{isGerman ? 'Recht auf Erhalt personenbezogener Daten soweit anwendbar' : 'Right to receive personal data where applicable'}</li>
              <li>{isGerman ? 'Widerspruchsrecht gegen bestimmte Verarbeitungstätigkeiten' : 'Right to object to certain processing activities'}</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              {isGerman
                ? 'Anfragen können an den Betreiber gesendet werden.'
                : 'Requests can be sent to the operator.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '9. Cookies und lokale Speicherung' : '9. Cookies and Local Storage'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Clansight verwendet keine Tracking- oder Werbe-Cookies. Die Website kann technisch notwendige Speicherung verwenden:'
                : 'Clansight does not use tracking or advertising cookies. The website may use technically necessary storage:'}
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>{isGerman ? 'Login-Sitzungsdaten' : 'Login session data'}</li>
              <li>{isGerman ? 'Spracheinstellungen' : 'Language settings'}</li>
              <li>{isGerman ? 'Benutzereinstellungen' : 'User preferences'}</li>
              <li>{isGerman ? 'Ausgewählte Saison oder Anzeigeoptionen' : 'Selected season or display options'}</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              {isGerman
                ? 'Diese Daten werden ausschliesslich für die Website-Funktionalität verwendet.'
                : 'This data is only used for website functionality.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '10. Datensicherheit' : '10. Data Security'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Wir verwenden angemessene technische und organisatorische Massnahmen zum Schutz der Daten, einschliesslich: HTTPS-Verschlüsselung, Zugriffsbeschränkungen, sichere Authentifizierung, regelmässige Backups. Vollständige Sicherheit kann nicht garantiert werden.'
                : 'We use appropriate technical and organisational measures to protect data, including: HTTPS encryption, access restrictions, secure authentication, regular backups. Complete security cannot be guaranteed.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '11. Änderungen' : '11. Changes'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Der Betreiber kann diese Datenschutzerklärung bei technischen, rechtlichen oder funktionalen Änderungen anpassen. Die aktuelle Version ist auf der Website verfügbar.'
                : 'The operator may update this privacy policy when technical, legal, or functional changes occur. The latest version is available on the website.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '12. Beschwerde' : '12. Complaints'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Wenn Sie der Ansicht sind, dass Ihre Daten unrechtmässig verarbeitet werden, können Sie die zuständige Datenschutzbehörde kontaktieren. Für die Schweiz: Eidgenössischer Datenschutz- und Öffentlichkeitsbeauftragter (EDÖB).'
                : 'If you believe your data is processed unlawfully, you may contact the competent data protection authority. For Switzerland: Federal Data Protection and Information Commissioner (FDPIC).'}
            </p>
          </section>

          <div className="mt-8 pt-8 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              {isGerman ? 'Stand: Juni 2026' : 'Last updated: June 2026'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
